import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Search, Trash2, Power, PowerOff, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { PageTitle, Card, inputCls, GoldButton, GhostButton } from "@/admin/ui";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
};

export const PublicUsersManager = () => {

const FragmentRow = ({ u, onReset, onToggle, onDelete, resetting, newPwd, setNewPwd, doReset, cancelReset, fmt }) => (
  <>
    <tr className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
      <td className="py-3 pr-4 text-white font-medium" data-testid={`pu-identifier-${u.id}`}>{u.identifier}</td>
      <td className="py-3 pr-4 text-white/60 uppercase text-xs">{u.identifier_type}</td>
      <td className="py-3 pr-4 text-white/60 text-xs">{fmt(u.created_at)}</td>
      <td className="py-3 pr-4 text-white/60 text-xs">{fmt(u.last_login_at)}</td>
      <td className="py-3 pr-4">
        <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${u.is_active ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "bg-red-500/15 text-red-300 border border-red-500/30"}`}>
          {u.is_active ? "Ativo" : "Inativo"}
        </span>
      </td>
      <td className="py-3 flex flex-wrap gap-2">
        <button
          onClick={onReset}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-[#F5C542]/15 border border-[#F5C542]/40 text-[#FFD84D] hover:bg-[#F5C542]/25"
          data-testid={`pu-reset-${u.id}`}
        >
          <KeyRound className="w-3 h-3" /> Nova senha
        </button>
        <button
          onClick={onToggle}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-white/[0.04] border border-white/10 text-white/70 hover:border-white/25"
          data-testid={`pu-toggle-${u.id}`}
        >
          {u.is_active ? <><PowerOff className="w-3 h-3" /> Desativar</> : <><Power className="w-3 h-3" /> Ativar</>}
        </button>
        <button
          onClick={onDelete}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20"
          data-testid={`pu-delete-${u.id}`}
        >
          <Trash2 className="w-3 h-3" /> Excluir
        </button>
      </td>
    </tr>
    {resetting && (
      <tr>
        <td colSpan={6} className="py-3 pl-4 pr-4">
          <div className="flex flex-wrap items-center gap-3 rounded-xl p-3" style={{ background: "rgba(245, 197, 66,0.06)", border: "1px solid rgba(245, 197, 66,0.28)" }}>
            <span className="text-xs text-white/70">Nova senha para <strong>{u.identifier}</strong>:</span>
            <input
              type="text"
              autoFocus
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              placeholder="Mínimo 6 caracteres"
              className={`${inputCls} max-w-xs`}
              data-testid={`pu-newpwd-${u.id}`}
            />
            <GoldButton onClick={doReset} data-testid={`pu-newpwd-confirm-${u.id}`}>Salvar senha</GoldButton>
            <GhostButton onClick={cancelReset}>Cancelar</GhostButton>
          </div>
        </td>
      </tr>
    )}
  </>
);

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(null); // uid
  const [newPwd, setNewPwd] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/public-users", { params: { q } });
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const doReset = async (uid) => {
    if (newPwd.length < 6) return toast.error("Senha mínima de 6 caracteres");
    try {
      await api.post(`/admin/public-users/${uid}/reset-password`, { new_password: newPwd });
      toast.success("Senha do usuário atualizada");
      setResetting(null);
      setNewPwd("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/public-users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "Usuário desativado" : "Usuário ativado");
      load();
    } catch { toast.error("Erro"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Excluir permanentemente ${u.identifier}?`)) return;
    try {
      await api.delete(`/admin/public-users/${u.id}`);
      toast.success("Usuário removido");
      load();
    } catch { toast.error("Erro"); }
  };

  return (
    <div data-testid="admin-public-users">
      <PageTitle
        title="Usuários"
        subtitle={`${total} conta(s) registrada(s) — pessoas que fizeram cadastro pra acessar o site`}
        action={<GhostButton onClick={load} data-testid="pu-refresh"><RefreshCw className="w-4 h-4 inline mr-1" /> Atualizar</GhostButton>}
      />

      <Card className="mb-6">
        <div className="relative">
          <Search className="w-4 h-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por e-mail ou telefone..."
            className={`${inputCls} pl-11`}
            data-testid="pu-search"
          />
        </div>
      </Card>

      <Card>
        {loading ? (
          <div className="text-center text-white/40 py-8">Carregando...</div>
        ) : users.length === 0 ? (
          <div className="text-center text-white/40 py-8 text-sm">Nenhum usuário registrado ainda.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-white/40 border-b border-white/[0.08]">
                  <th className="py-3 pr-4">Identificador</th>
                  <th className="py-3 pr-4">Tipo</th>
                  <th className="py-3 pr-4">Criado em</th>
                  <th className="py-3 pr-4">Último login</th>
                  <th className="py-3 pr-4">Status</th>
                  <th className="py-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <FragmentRow
                    key={u.id}
                    u={u}
                    onReset={() => { setResetting(u.id); setNewPwd(""); }}
                    onToggle={() => toggleActive(u)}
                    onDelete={() => remove(u)}
                    resetting={resetting === u.id}
                    newPwd={newPwd}
                    setNewPwd={setNewPwd}
                    doReset={() => doReset(u.id)}
                    cancelReset={() => { setResetting(null); setNewPwd(""); }}
                    fmt={fmt}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};
